import { type JsonRpcProvider } from '@ethersproject/providers';
import { Contract, type ContractInterface } from '@ethersproject/contracts';
import {
    FirstDibsAuction as auctionArtifact,
    FirstDibsAuctionV2 as auctionV2Artifact,
    FirstDibsToken as tokenArtifact,
    FirstDibsTokenV2 as tokenArtifactV2,
    FirstDibsMarketSettings as marketSettingsArtifact,
    FirstDibsMarketSettingsV2 as marketSettingsV2Artifact,
    GenericERC721Token as genericERC721Artifact,
    GenericERC1155Token as genericERC1155Artifact,
    FirstDibsBalances as balancesArtifact,
    RoyaltyRegistry as royaltyRegistryArtifact,
    RoyaltyEngineV1 as royaltyEngineV1Artifact,
    SplitMain as splitMainArtifact,
} from '1stdibs-contracts';
import { getAddress } from '1stdibs-contracts/addresses';
// import {
//     type FirstDibsAuction,
//     type FirstDibsAuctionV2,
//     type FirstDibsToken,
//     type FirstDibsMarketSettings,
//     type FirstDibsMarketSettingsV2,
//     type FirstDibsTokenV2,
//     type GenericERC721Token,
//     type GenericERC1155Token,
//     type SplitMain,
//     type RoyaltyEngineV1,
//     type FirstDibsBalances,
// } from '1stdibs-contracts/typechain';
import {
    TOKEN_CONTRACT_NAME,
    AUCTION_CONTRACT_NAME,
    MARKET_SETTINGS_CONTRACT_NAME,
    MARKET_SETTINGS_V2_CONTRACT_NAME,
    TOKEN_V2_CONTRACT_NAME,
    GENERIC_TOKEN_CONTRACT_NAME,
    GENERIC_1155_TOKEN_CONTRACT_NAME,
    AUCTION_V2_CONTRACT_NAME,
    BALANCE_CONTRACT_NAME,
    ROYALTY_REGISTRY,
    ROYALTY_ENGINE_V1,
    SPLIT_MAIN_NAME,
} from '1stdibs-contracts/contractNames';
import { chains } from '../supportedChains';

// 1stdibs-contracts not compatible with verbatimModuleSyntax
type FirstDibsAuction = $TSFixMe;
type FirstDibsAuctionV2 = $TSFixMe;
type FirstDibsToken = $TSFixMe;
type FirstDibsMarketSettings = $TSFixMe;
type FirstDibsMarketSettingsV2 = $TSFixMe;
type FirstDibsTokenV2 = $TSFixMe;
type GenericERC721Token = $TSFixMe;
type GenericERC1155Token = $TSFixMe;
type SplitMain = $TSFixMe;
type RoyaltyEngineV1 = $TSFixMe;
type FirstDibsBalances = $TSFixMe;

const contracts = new Map<string, { abi: ContractInterface }>([
    [TOKEN_CONTRACT_NAME, tokenArtifact],
    [TOKEN_V2_CONTRACT_NAME, tokenArtifactV2],
    [AUCTION_CONTRACT_NAME, auctionArtifact],
    [AUCTION_V2_CONTRACT_NAME, auctionV2Artifact],
    [MARKET_SETTINGS_CONTRACT_NAME, marketSettingsArtifact],
    [MARKET_SETTINGS_V2_CONTRACT_NAME, marketSettingsV2Artifact],
    [GENERIC_TOKEN_CONTRACT_NAME, genericERC721Artifact],
    [GENERIC_1155_TOKEN_CONTRACT_NAME, genericERC1155Artifact],
    [BALANCE_CONTRACT_NAME, balancesArtifact],
    [ROYALTY_REGISTRY, royaltyRegistryArtifact],
    [ROYALTY_ENGINE_V1, royaltyEngineV1Artifact],
    [SPLIT_MAIN_NAME, splitMainArtifact],
]);

const royaltyEngineAddressMap: { [key: number]: string } = {
    1: '0x0385603ab55642cb4dd5de3ae9e306809991804f', // mainnet address
    3: '0xFf5A6F7f36764aAD301B7C9E85A5277614Df5E26', // ropsten address
    4: '0x8d17687ea9a6bb6efA24ec11DcFab01661b2ddcd', // rinkeby address
    5: '0xe7c9Cb6D966f76f3B5142167088927Bf34966a1f', // goerli address
};

function getThirdPartyAddress(
    contractName: string,
    chainId: number
): { isThirdPartyContract: boolean; address: string | null } {
    if (contractName === SPLIT_MAIN_NAME) {
        return {
            isThirdPartyContract: true,
            address: '0x2ed6c4B5dA6378c7897AC67Ba9e43102Feb694EE',
        };
    } else if (contractName === ROYALTY_ENGINE_V1) {
        return { isThirdPartyContract: true, address: royaltyEngineAddressMap[chainId] || null };
    }
    return { isThirdPartyContract: false, address: null };
}

async function getContract<T extends Contract>(
    contractName:
        | typeof TOKEN_CONTRACT_NAME
        | typeof TOKEN_V2_CONTRACT_NAME
        | typeof AUCTION_CONTRACT_NAME
        | typeof AUCTION_V2_CONTRACT_NAME
        | typeof MARKET_SETTINGS_CONTRACT_NAME
        | typeof MARKET_SETTINGS_V2_CONTRACT_NAME
        | typeof GENERIC_TOKEN_CONTRACT_NAME
        | typeof GENERIC_1155_TOKEN_CONTRACT_NAME
        | typeof BALANCE_CONTRACT_NAME
        | typeof SPLIT_MAIN_NAME
        | typeof ROYALTY_ENGINE_V1,
    provider: JsonRpcProvider,
    chainId: number
): Promise<T | null> {
    const chainName = chains.get(chainId) || `chain id (${chainId}) unsupported`;
    const { abi } = contracts.get(contractName) || {};
    if (abi) {
        const { isThirdPartyContract, address: thirdPartyAddress } = getThirdPartyAddress(
            contractName,
            chainId
        );
        if (isThirdPartyContract && !thirdPartyAddress) {
            return null;
        }
        const contractAddress =
            isThirdPartyContract && thirdPartyAddress
                ? thirdPartyAddress
                : await getAddress(contractName, chainName.toLowerCase());
        return new Contract(contractAddress, abi, provider) as unknown as T;
    }
    return null;
}
type ContractArgs = [provider: JsonRpcProvider, chainId: number];

export async function getAuctionContract(
    provider: JsonRpcProvider,
    chainId: number
): Promise<FirstDibsAuction | null> {
    return await getContract<FirstDibsAuction>(AUCTION_CONTRACT_NAME, provider, chainId);
}

export async function getAuctionV2Contract(
    provider: JsonRpcProvider,
    chainId: number
): Promise<FirstDibsAuctionV2 | null> {
    try {
        return await getContract<FirstDibsAuctionV2>(AUCTION_V2_CONTRACT_NAME, provider, chainId);
    } catch {
        return null;
    }
}

export const getTokenContract = (...args: ContractArgs): Promise<FirstDibsToken | null> =>
    getContract(TOKEN_CONTRACT_NAME, ...args);

export const getTokenV2Contract = (...args: ContractArgs): Promise<FirstDibsTokenV2 | null> =>
    getContract(TOKEN_V2_CONTRACT_NAME, ...args);

export async function getGenericTokenContract(
    provider: JsonRpcProvider,
    address: string
): Promise<GenericERC721Token | null> {
    const { abi } = contracts.get(GENERIC_TOKEN_CONTRACT_NAME) || {};
    if (abi) {
        return new Contract(address, abi, provider) as GenericERC721Token;
    }
    return null;
}

export async function getGeneric1155TokenContract(
    provider: JsonRpcProvider,
    address: string
): Promise<GenericERC1155Token | null> {
    const { abi } = contracts.get(GENERIC_1155_TOKEN_CONTRACT_NAME) || {};
    if (abi) {
        return new Contract(address, abi, provider) as GenericERC1155Token;
    }
    return null;
}

export async function getMarketSettingsContract(
    provider: JsonRpcProvider,
    chainId: number
): Promise<FirstDibsMarketSettings | null> {
    return await getContract<FirstDibsMarketSettings>(
        MARKET_SETTINGS_CONTRACT_NAME,
        provider,
        chainId
    );
}

export async function getMarketSettingsV2Contract(
    provider: JsonRpcProvider,
    chainId: number
): Promise<FirstDibsMarketSettingsV2 | null> {
    try {
        return await getContract<FirstDibsMarketSettingsV2>(
            MARKET_SETTINGS_V2_CONTRACT_NAME,
            provider,
            chainId
        );
    } catch {
        return null;
    }
}

export async function getBalancesContract(
    provider: JsonRpcProvider,
    chainId: number
): Promise<FirstDibsBalances | null> {
    try {
        return await getContract<FirstDibsBalances>(BALANCE_CONTRACT_NAME, provider, chainId);
    } catch {
        return null;
    }
}

export async function getSplitMainContract(
    provider: JsonRpcProvider,
    chainId: number
): Promise<SplitMain | null> {
    return await getContract<SplitMain>(SPLIT_MAIN_NAME, provider, chainId);
}

export async function getRoyaltyEngineContract(
    provider: JsonRpcProvider,
    chainId: number
): Promise<RoyaltyEngineV1 | null> {
    return await getContract<RoyaltyEngineV1>(ROYALTY_ENGINE_V1, provider, chainId);
}
